import { useState } from 'react';
import { ChevronDown, PlayCircle } from 'lucide-react';
import { buttonClasses, Card, SectionHeader } from '@pixlide/ui';
import { HelpSidebar } from '@/components/support/HelpSidebar';
import { SUPPORT_MAILTO } from '@/lib/support';

const FAQ_ITEMS = [
  {
    question: 'Que faire si le spot ne me convient pas ?',
    answer:
      "Malgré notre expérience et notre savoir-faire, il est tout à fait possible que le spot publicitaire ne vous convienne pas. Dans ce cas, nous vous proposons rapidement une nouvelle version. Vous avez la possibilité jusqu'à 3 retours selon la formule choisie.",
  },
  {
    question: 'Si mon visuel source est un fichier Indesign, Illustrator ou Photoshop ?',
    answer:
      "Aucun souci : transmettez-nous le fichier source dans son format d'origine, notre équipe se charge de l'adapter au format vidéo.",
  },
  {
    question: 'Pouvez-vous faire un spot à partir de vidéos ?',
    answer: 'Oui, vous pouvez nous envoyer vos propres rushs vidéo en complément ou à la place de visuels statiques.',
  },
  {
    question: 'Pouvez-vous changer les informations du visuel de base ?',
    answer:
      "Oui, précisez les modifications souhaitées (textes, prix, coordonnées) dans votre brief et nous les appliquons avant le rendu final.",
  },
  {
    question: 'Peut-on ajouter du son au spot pub ?',
    answer: "Oui, une piste audio ou une voix off peut être ajoutée selon la formule choisie.",
  },
  {
    question: "Qu'est-ce qu'un repiquage / déclinaison ?",
    answer:
      "Un repiquage reprend une création existante pour l'adapter à un nouveau format ou une nouvelle offre, sans repartir d'une page blanche.",
  },
] as const;

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-ink-100/70 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 py-4 text-left text-sm font-semibold text-ink-900"
      >
        {question}
        <ChevronDown
          aria-hidden
          className={`size-4 shrink-0 text-ink-500 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open ? <p className="pb-4 text-sm text-ink-500">{answer}</p> : null}
    </div>
  );
}

/**
 * Support : vidéo de prise en main, FAQ et moyens de contact.
 *
 * La FAQ n'a pas (encore) de source API dédiée : le contenu est éditorial et changera
 * rarement, on l'a donc figé ici plutôt que d'inventer un endpoint pour six questions.
 * Les canaux de contact réutilisent volontairement les mêmes intitulés que le tableau de
 * bord (Chat Support / Email Support) pour ne pas dupliquer un vocabulaire différent.
 */
export function SupportPage() {
  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <SectionHeader title="Support" />

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-8">
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-ink-900">Découvrez notre vidéo explicative</h3>
            <p className="mt-1 text-sm text-ink-500">
              Cette vidéo vous présente de manière claire et concise toutes les informations essentielles pour
              bien utiliser la plateforme.
            </p>

            <div className="mt-4 flex aspect-video items-center justify-center rounded-input bg-ink-900">
              <PlayCircle aria-hidden className="size-14 text-white/80" strokeWidth={1} />
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold text-ink-900">FAQ – Questions fréquentes</h3>
            <p className="mt-1 text-sm text-ink-500">
              Consultez notre foire aux questions pour découvrir les réponses aux interrogations les plus
              courantes de nos clients. Vous y trouverez peut-être la solution à votre problème.
            </p>

            <div className="mt-4">
              {FAQ_ITEMS.map((item) => (
                <FaqItem key={item.question} {...item} />
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-ink-100/70 pt-5">
              <div>
                <p className="text-sm font-semibold text-ink-900">Vous avez d'autres questions ?</p>
                <p className="text-sm text-ink-500">Contactez notre équipe d'assistance pour toute question.</p>
              </div>
              <a href={SUPPORT_MAILTO} className={buttonClasses({ variant: 'secondary', size: 'sm' })}>
                Contactez-nous
              </a>
            </div>
          </Card>
        </div>

        <HelpSidebar />
      </div>
    </div>
  );
}
